import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Container,
  Icon,
  SpaceBetween,
} from "@amzn/awsui-components-react";

import { RootState } from "../app/rootReducer";
import {
  PREDEFINED_FILTERS,
  PredefinedFilter,
} from "../filterBar/predefinedFilters";
import { setActivePredefined } from "../filterBar/savedFiltersSlice";
import {
  removeNotification,
  RESTRICTED_ASINS_NOTIFICATION_KEY,
} from "../notification/notificationConnector";
import {
  PropertyFilterGroup,
  setQueryMode,
  setQueryPropertyFilters,
  setQueryPropertyFiltersForceRehydrate,
  setQueryString,
} from "../searchSideBar/searchSideBarConnector";
import { trackButtonClick } from "../telemetry/telemetry";

interface NoFiltersPanelProps {
  /**
   * Noun that appears in the heading ("Select filters to view {noun}")
   * and the intro sentence. Required so each hosting page states what
   * it displays.
   */
  readonly dataNoun: string;
  /**
   * Called after a quick filter is applied so the hosting page can
   * reset its own paginator. Kept optional so consumers that don't
   * paginate can omit it.
   */
  readonly onResetPagination?: () => void;
}

/**
 * Zero-state panel shown in place of a page's main content when no
 * filter is applied. Renders the three-step guidance and
 * quick-filter buttons so users can start a query with one click.
 */
export const NoFiltersPanel: React.FC<NoFiltersPanelProps> = ({
  dataNoun,
  onResetPagination,
}) => {
  const dispatch = useDispatch();

  // The "Restricted ASINs" flashbar is non-dismissible and pinned by
  // a previous search. It refers to rows that aren't on-screen while
  // this empty state is up, so drop it as soon as it appears — not
  // only on mount. Subscribing to the keyed notification catches the
  // case where an in-flight fetch response, or a partial-clear code
  // path that empties queryString without touching notifications,
  // adds the flashbar after NoFiltersPanel has already mounted.
  const restrictedAsinsNotification = useSelector(
    (state: RootState) =>
      state.notification.notificationItems[RESTRICTED_ASINS_NOTIFICATION_KEY]
  );
  useEffect(() => {
    if (restrictedAsinsNotification !== undefined) {
      dispatch(removeNotification(RESTRICTED_ASINS_NOTIFICATION_KEY));
    }
  }, [dispatch, restrictedAsinsNotification]);

  const applyPropertyFilter = useCallback(
    (filterGroup: PropertyFilterGroup) => {
      dispatch(setQueryMode("PropertyFilter"));
      dispatch(setQueryPropertyFilters(filterGroup));
      dispatch(setQueryPropertyFiltersForceRehydrate(true));
      dispatch(setQueryString());
      onResetPagination?.();
    },
    [dispatch, onResetPagination]
  );

  const onQuickFilterClick = useCallback(
    (pf: PredefinedFilter) => {
      trackButtonClick("noFiltersPanel", "QuickFilter", {
        filterName: pf.label,
      });
      applyPropertyFilter(pf.buildFilters());
      dispatch(setActivePredefined(pf.id));
    },
    [applyPropertyFilter, dispatch]
  );

  return (
    <Container data-testid="validation-no-filters-panel">
      <Box padding={{ vertical: "xxl" }} textAlign="center">
        <SpaceBetween size="m" alignItems="center">
          <Box variant="div">
            <Icon name="search" size="big" variant="subtle" />
          </Box>
          <Box variant="h2">Select filters to view {dataNoun}</Box>
          <Box variant="p" color="text-body-secondary">
            Select one of the Saved Filter Views or select at least one filter
            using the Property Filter to view {dataNoun}.
          </Box>

          <Box padding={{ top: "s" }}>
            <SpaceBetween size="s" alignItems="center">
              <Box variant="div">
                <Box variant="strong">① Select a Saved Filter View</Box>
                <Box variant="p" color="text-body-secondary">
                  from the dropdown (e.g., &ldquo;This Week&rsquo;s
                  Launches&rdquo;)
                </Box>
              </Box>
              <Box variant="div">
                <Box variant="strong">② Or add filters</Box>
                <Box variant="p" color="text-body-secondary">
                  using the Property Filter to search by ASIN, Program,
                  Marketplace, or any attribute
                </Box>
              </Box>
              <Box variant="div">
                <Box variant="strong">③ Click Submit</Box>
                <Box variant="p" color="text-body-secondary">
                  to load results
                </Box>
              </Box>
            </SpaceBetween>
          </Box>

          {PREDEFINED_FILTERS.length > 0 && (
            <Box
              padding={{ top: "s" }}
              data-testid="validation-no-filters-quick-filters"
            >
              <SpaceBetween size="xs" alignItems="center">
                <Box variant="p" color="text-body-secondary">
                  Quick filters:
                </Box>
                <SpaceBetween direction="horizontal" size="xs">
                  {PREDEFINED_FILTERS.map((pf) => (
                    <Button
                      key={pf.id}
                      data-testid={`validation-no-filters-quick-filter-${pf.id}`}
                      iconName="calendar"
                      onClick={() => onQuickFilterClick(pf)}
                    >
                      {pf.label}
                    </Button>
                  ))}
                </SpaceBetween>
              </SpaceBetween>
            </Box>
          )}
        </SpaceBetween>
      </Box>
    </Container>
  );
};

export default NoFiltersPanel;
